import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { BouncySumsGame } from '@/features/bouncySums';
import { LaneChooserGame } from '@/features/laneChooser';
import { LetterPartyGame } from '@/features/letterParty';
import { MeasurementGame } from '@/features/measurementGame';
import { TimeGame } from '@/features/timeGame';
import { TracingGame } from '@/features/tracingGame';
import { clearGameState, getGameDataSuccess } from '../store/actions';
import { GAME_TYPES } from '../store/constants';
import { getRequestedGameType, getSupportedGameType, LANE_CHOOSER_THEME_QUERY_PARAM } from '../queryParams';
import { getDummyGameData } from '../dummyGameData';

import GameSession from './GameSession';

import type { BouncySumsGameData } from '@/features/bouncySums';
import type { MeasurementGameData } from '@/features/measurementGame';
import type { TracingGameData } from '@/features/tracingGame';
import type { GameData, GameFinishHandler } from '../types';
import type { GameWrapperProps } from './GameWrapper';

const DummyGameWrapper = (props: GameWrapperProps) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const [ready, setReady] = useState(false);
	const searchParams = new URLSearchParams(location.search);
	const requestedGameType = getRequestedGameType(location.pathname, searchParams);
	const handleFinish = useCallback<GameFinishHandler>(
		(event, onFinished) => {
			props.onFinish?.(event);
			onFinished?.();
		},
		[props.onFinish],
	);

	useEffect(() => {
		const dummyGameData = getDummyGameData(requestedGameType);
		const gameData =
			typeof structuredClone === 'function'
				? structuredClone(dummyGameData)
				: JSON.parse(JSON.stringify(dummyGameData));
		dispatch(getGameDataSuccess(gameData as GameData));
		setReady(true);

		return () => {
			dispatch(clearGameState());
		};
	}, [dispatch, requestedGameType]);

	if (!ready) return null;

	return (
		<GameSession {...props} onFinish={handleFinish}>
			{(gameProps) => {
				const gameType =
					requestedGameType ?? getSupportedGameType(gameProps.gameData.game?.type ?? gameProps.gameData.type);
				switch (gameType) {
					case GAME_TYPES.BOUNCY_SUMS_GAME:
						return (
							<BouncySumsGame
								{...gameProps}
								gameData={gameProps.gameData as BouncySumsGameData}
								highScore={(gameProps.gameData as BouncySumsGameData).high_score ?? 0}
							/>
						);
					case GAME_TYPES.LANE_CHOOSER_GAME:
						return (
							<LaneChooserGame
								{...gameProps}
								themeKey={searchParams.get(LANE_CHOOSER_THEME_QUERY_PARAM)}
							/>
						);
					case GAME_TYPES.LETTER_PARTY_GAME:
						return <LetterPartyGame key={gameProps.gameData.id ?? 'letter-party'} {...gameProps} />;
					case GAME_TYPES.TRACING_GAME:
						return (
							<TracingGame {...gameProps} gameData={gameProps.gameData as unknown as TracingGameData} />
						);
					case GAME_TYPES.MEASUREMENT_GAME:
						return <MeasurementGame {...gameProps} gameData={gameProps.gameData as MeasurementGameData} />;
					case GAME_TYPES.TIME_GAME:
					default:
						return <TimeGame key={gameProps.gameData.id ?? 'time-game'} {...gameProps} />;
				}
			}}
		</GameSession>
	);
};

export default DummyGameWrapper;
